import React from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import {
  forwardingOptions,
  getIntegrationTypeName,
  IntegrationLayoutEntry,
  IntegrationType,
} from "@common/models/integrations";
import { Clickup } from "@common/models/integrations/Clickup.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { SpaceOption, useClickupIntegrationFormConsumer } from "./consumers/useClickupIntegrationFormConsumer";

interface ClickupIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Clickup;
  selectedAppId: string;
  onFinish: () => void;
}

export const ClickupIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: ClickupIntegrationModalProps) => {
  const {
    disabled,
    loading,
    listOption,
    listOptions,
    spaceOption,
    spaceOptions,
    forwardingOption,
    handleFormChange,
    handleFormSubmit,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useClickupIntegrationFormConsumer({ integration, selectedAppId, onFinish });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, spaceOption, listOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.CLICKUP}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.CLICKUP)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.CLICKUP)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="spaceOption"
          value={spaceOption}
          options={spaceOptions}
          placeholder="Please choose"
          label="Space"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />
        <SelectDropdown
          name="listOption"
          value={listOption}
          options={listOptions}
          placeholder="Please choose"
          label="List"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || !spaceOption || loading}
        />
        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  spaceOption: Option,
  listOption: Option,
  forwardingOption: Option,
  integrationLayout: IntegrationLayoutEntry[],
  integration?: Clickup,
) => {
  if (disabled) return true;
  if (!spaceOption || !forwardingOption) return true;

  const parsedSpaceOption = JSON.parse(spaceOption.value) as SpaceOption;
  if (
    integration &&
    listOption &&
    listOption.value !== "" &&
    (parsedSpaceOption.spaceId !== integration?.space_id ||
      !!forwardingOption.value !== integration?.automatic_forward ||
      listOption.value !== integration?.list_id)
  ) {
    return false;
  }

  // Check if integration layout has changed
  return !(integration && hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout));
};

const hasIntegrationLayoutChanged = (
  currentLayout: IntegrationLayoutEntry[],
  originalLayout?: IntegrationLayoutEntry[],
): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
